
import React from 'react'
import styled from 'styled-components'
import { useTable, useFilters, useSortBy, useGlobalFilter, usePagination } from 'react-table'
import matchSorter from 'match-sorter'
import {CSVLink} from "react-csv";
 

const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, ...rest }, ref) => {
    const defaultRef = React.useRef();
    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return <input type="checkbox" ref={resolvedRef} {...rest} />;
  }
);

// Define a default UI for filtering
function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter
}) {
  return (
    <span>
      Buscar en cualquier campo:{" "}
      <input
        className="my-2 rounded border"
        value={globalFilter || ""}
        onChange={e => {
          setGlobalFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
        }}
        placeholder={`Ingrese palabras`}
        style={{
          fontSize: "1.1rem",
          border: "0"
        }}
      />
    </span>
  );
}

// Define a default UI for filtering
function DefaultColumnFilter({
  column: { filterValue, preFilteredRows, setFilter }
}) {
  return (
    <input
      className="form-control form-control-sm"
      value={filterValue || ""}
      onChange={e => {
        setFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
      }}
      placeholder={`Ingrese búsqueda`}
    />
  );
}

export function DateSorter(a, b, columnId) {
  var a_date = a.values[columnId];
  var b_date = b.values[columnId];
  if (a_date === b_date) {
    return 0;
  }
  var dateParts = a_date.split("/");
  a_date = new Date(dateParts[2], dateParts[1] - 1, dateParts[0]);
  dateParts = b_date.split("/");
  b_date = new Date(+dateParts[2], dateParts[1] - 1, +dateParts[0]);
  return a_date > b_date ? 1 : -1;
}

// This is a custom filter UI for selecting
// a unique option from a list
export function SelectColumnFilter({
  column: { filterValue, setFilter, preFilteredRows, id }
}) {
  // Calculate the options for filtering
  // using the preFilteredRows
  const options = React.useMemo(() => {
    const options = new Set();
    preFilteredRows.forEach(row => {
      options.add(row.values[id]);
    });
    return [...options.values()];
  }, [id, preFilteredRows]);

  // Render a multi-select box
  return (
    <select
      className="form-control form-control-sm"
      value={filterValue}
      onChange={e => {
        setFilter(e.target.value || undefined);
      }}
    >
      <option value="">Todos</option>
      {options.map((option, i) => (
        <option key={i} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
}

// This is a custom filter UI for selecting
// a unique option from a list
export function CsvColumnFilter({
  column: { filterValue, setFilter, preFilteredRows, id }
}) {
  // Calculate the options for filtering
  // using the preFilteredRows
  const options = React.useMemo(() => {
    const options = new Set();
    preFilteredRows.forEach(row => {
      options.add(row.values[id]);
    });
    return [...options.values()];
  }, [id, preFilteredRows]);

  // Render a multi-select box
  return (
    <select
      className="form-control form-control-sm"
      value={filterValue}
      onChange={e => {
        setFilter(e.target.value || undefined);
      }}
    >
      <option value="">Todos</option>
      {options
        .filter(o => !o.includes(","))
        .map((option, i) => (
          <option key={i} value={option}>
            {option}
          </option>
        ))}
    </select>
  );
}

// This is a custom filter UI that uses a
// slider to set the filter value between a column's
// min and max values
export function SliderColumnFilter({
  column: { filterValue, setFilter, preFilteredRows, id }
}) {
  // Calculate the min and max
  // using the preFilteredRows

  const [min, max] = React.useMemo(() => {
    let min = preFilteredRows.length ? preFilteredRows[0].values[id] : 0;
    let max = preFilteredRows.length ? preFilteredRows[0].values[id] : 0;
    preFilteredRows.forEach(row => {
      min = Math.min(row.values[id], min);
      max = Math.max(row.values[id], max);
    });
    return [min, max];
  }, [id, preFilteredRows]);

  return (
    <>
      <input
        type="range"
        min={min}
        max={max}
        value={filterValue || min}
        onChange={e => {
          setFilter(parseInt(e.target.value, 10));
        }}
      />
      <button onClick={() => setFilter(undefined)}>Off</button>
    </>
  );
}

// This is a custom UI for our 'between' or number range
// filter. It uses two number boxes and filters rows to
// ones that have values between the two
export function NumberRangeColumnFilter({
  column: { filterValue = [], preFilteredRows, setFilter, id }
}) {
  const [min, max] = React.useMemo(() => {
    let min = preFilteredRows.length ? preFilteredRows[0].values[id] : 0;
    let max = preFilteredRows.length ? preFilteredRows[0].values[id] : 0;
    preFilteredRows.forEach(row => {
      min = Math.min(row.values[id], min);
      max = Math.max(row.values[id], max);
    });
    return [min, max];
  }, [id, preFilteredRows]);

  return (
    <div
      style={{
        display: "flex"
      }}
    >
      <input
        value={filterValue[0] || ""}
        type="number"
        onChange={e => {
          const val = e.target.value;
          setFilter((old = []) => [
            val ? parseInt(val, 10) : undefined,
            old[1]
          ]);
        }}
        placeholder={`Min (${min})`}
        style={{
          width: "70px",
          marginRight: "0.5rem"
        }}
      />
      to
      <input
        value={filterValue[1] || ""}
        type="number"
        onChange={e => {
          const val = e.target.value;
          setFilter((old = []) => [
            old[0],
            val ? parseInt(val, 10) : undefined
          ]);
        }}
        placeholder={`Max (${max})`}
        style={{
          width: "70px",
          marginLeft: "0.5rem"
        }}
      />
    </div>
  );
}

function fuzzyTextFilterFn(rows, id, filterValue) {
  return matchSorter(rows, filterValue, { keys: [row => row.values[id]] });
}

// Let the table remove the filter if the string is empty
fuzzyTextFilterFn.autoRemove = val => !val;
// Define a custom filter filter function!
function filterGreaterThan(rows, id, filterValue) {
  return rows.filter(row => {
    const rowValue = row.values[id];
    return rowValue >= filterValue;
  });
}

// This is an autoRemove method on the filter function that
// when given the new filter value and returns true, the filter
// will be automatically removed. Normally this is just an undefined
// check, but here, we want to remove the filter if it's not a number
filterGreaterThan.autoRemove = val => typeof val !== "number";

function Table({columns, data, rowsShown,csvData}) {
    const defaultColumn = React.useMemo(
      () => ({
        // Let's set up our default Filter UI
        Filter: DefaultColumnFilter,
      }),
      []
    )
    
    const filterTypes = React.useMemo(
      () => ({
        // Add a new fuzzyTextFilterFn filter type.
        fuzzyText: fuzzyTextFilterFn,
        // Or, override the default text filter to use
        // "startWith"
        /*text: (rows, id, filterValue) => {
          return rows.filter(row => {
            const rowValue = row.values[id]
            return rowValue !== undefined
              ? String(rowValue)
                  .toLowerCase()
                  .startsWith(String(filterValue).toLowerCase())
              : true
          })
        },*/
        text:fuzzyTextFilterFn
      }),
      []
    )

    const sortTypes = React.useMemo(
      ()=>({dateSort: DateSorter}),[]  
    )
    
    const {
      getTableProps,
      getTableBodyProps,
      headerGroups,
      prepareRow,
      page, // Instead of using 'rows', we'll use page,
      // which has only the rows for the active page
  
      // The rest of these things are super handy, too ;)
      canPreviousPage,
      canNextPage,
      pageOptions,
      pageCount,
      gotoPage,
      nextPage,
      previousPage,
      setPageSize,
      state: { pageIndex, pageSize },
      state,
      visibleColumns,
      allColumns,
      getToggleHideAllColumnsProps,
      preGlobalFilteredRows,
      setGlobalFilter,
    } = useTable(
      {
        columns,
        data,
        defaultColumn, // Be sure to pass the defaultColumn option
        filterTypes,
        sortTypes,
        initialState: { pageIndex: 0, pageSize:rowsShown }
      },
      useFilters, // useFilters!
      useGlobalFilter, // useGlobalFilter!
      useSortBy,
      usePagination
    )
  
    // Render Data Table UI
    return (
      <>
        <div style={{background: '#fff', display: 'block', overflow: 'scroll',maxHeight:'80vh', border: "3px solid #fe2c55"}} className="px-4 mb-4 rounded">              
          <table {...getTableProps()} className="table table-striped " >              
            <thead>
              <tr>
                <th
                  colSpan={visibleColumns.length}
                  style={{
                    textAlign: 'left',
                  }}
                >
                  <GlobalFilter
                    preGlobalFilteredRows={preGlobalFilteredRows}
                    globalFilter={state.globalFilter}
                    setGlobalFilter={setGlobalFilter}
                  />
                </th>
              </tr>
              {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                  <th {...column.getHeaderProps(column.getSortByToggleProps())} scope="col" style={{minWidth:column.width,maxWidth:column.maxWidth}} >
                    {column.render('Header')}
                    {column.sortable &&
                      <span style={{fontSize:'24px',fontWeight:'normal',lineHeight:'14px'}}>
                      {column.isSorted
                        ? column.isSortedDesc
                          ? "  " + String.fromCharCode(8659)
                          : "  " + String.fromCharCode(8657)
                        : "  " + String.fromCharCode(8655)}
                    </span>
                    }
                  <div>
                    {!column.noFilter ? column.render("Filter") : null}
                  </div>
                </th>
              ))}
            </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row, i) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map(cell => {
                    var content = cell.render("Cell");
                    if (
                      content.props.cell.column.link &&
                      content.props.cell.value !== ""
                    ) {
                      return (
                        <td {...cell.getCellProps()}>
                          <a
                            href={content.props.cell.value}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Link
                          </a>
                        </td>
                      );
                    } else {
                      return <td {...cell.getCellProps()}>{content}</td>;
                    }
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className=" pb-2">
          <label className="font-weight-bold">Ocultar columnas</label>
          <br />
          <div className="form-check form-check-inline">
            <IndeterminateCheckbox {...getToggleHideAllColumnsProps()} /> Todas
          </div>
          {allColumns.map(column => (
            <div key={column.id} className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="checkbox"
                {...column.getToggleHiddenProps()}
              />
              <label className="form-check-label" htmlFor="inlineCheckbox1">
                {column.Header}
              </label>
            </div>
          ))}
          <br />
        </div>
      </div>
      {/* Pagination */}
      <div className="text-center paginator">
        <button
          onClick={() => gotoPage(0)}
          disabled={!canPreviousPage}
          className="btn btn-danger font-weight-bold  bg-main"
        >
          {"<<"}
        </button>{" "}
        <button
          onClick={() => previousPage()}
          disabled={!canPreviousPage}
          className="btn btn-danger font-weight-bold  bg-main"
        >
          {"<"}
        </button>{" "}
        <button
          onClick={() => nextPage()}
          disabled={!canNextPage}
          className="btn btn-danger font-weight-bold  bg-main"
        >
          {">"}
        </button>{" "}
        <button
          onClick={() => gotoPage(pageCount - 1)}
          disabled={!canNextPage}
          className="btn btn-danger font-weight-bold bg-main"
        >
          {">>"}
        </button>{" "}
        <span className="text-white">
          Página{" "}
          <strong>
            {pageIndex + 1} de {pageOptions.length}
          </strong>{" "}
        </span>
        <span className="text-white">
          | Ir a la página:{" "}
          <input
            className="form-control d-inline-block"
            type="number"
            defaultValue={pageIndex + 1}
            onChange={e => {
              const page = e.target.value ? Number(e.target.value) - 1 : 0;
              gotoPage(page);
            }}
            />
            </span>{" "}
            <select
              className="form-control d-inline-block"
              value={pageSize}
              onChange={e => {
                setPageSize(Number(e.target.value));
              }}
            >
            {[5,10,25,50,100].map(pageSize => (
              <option key={pageSize} value={pageSize}>
                Mostrar {pageSize}
              </option>
            ))}
          </select>
        <CSVLink data={csvData} filename='Base de datos - Latam coronavirus.csv' className="btn btn-danger bg-main ml-2" >Descargar datos</CSVLink>
        </div>

      </>
  
    )
  }

export default Table;
